import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Database, Cloud, BarChart3, LineChart, Layers } from 'lucide-react';

const connectedSources = [
  { name: 'Amazon Ads', meta: 'SP, SB, SD, DSP', icon: Database },
  { name: 'Seller Central', meta: 'Account, inventory, orders', icon: Cloud },
  { name: 'Vendor Central', meta: 'POs, chargebacks, retail', icon: BarChart3 },
  { name: 'Amazon Marketing Cloud', meta: 'Cross-channel attribution', icon: LineChart },
  { name: 'DSP', meta: 'Display, video, audiences', icon: Layers },
];

export default function ProductRevealSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#05060B] py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.1)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-6xl mx-auto">
          <div 
            className={`text-center mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
              Data Intelligence
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              All your data,{' '}
              <span className="text-cyan-400 cyan-glow">connected.</span>
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              We integrate with every major Amazon data source to give you a complete picture of your business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {connectedSources.map((source, index) => (
              <div
                key={index}
                className={`glass-card p-6 text-center group hover-lift ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-500/20 transition-colors">
                  <source.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                  {source.name}
                </h3>
                <p className="text-xs text-dark-500">{source.meta}</p>
              </div>
            ))}
          </div>

          <div 
            className={`mt-16 relative transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div className="glass-card p-8">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-xl text-white mb-4">
                    Unified Intelligence Layer
                  </h3>
                  <p className="text-dark-400 mb-6">
                    Our platform normalizes data from all sources into a single view. No more switching between dashboards or reconciling conflicting metrics.
                  </p>
                  <ul className="space-y-3">
                    {['Real-time data sync', 'Automated anomaly detection', 'Cross-source attribution'].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-dark-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-1 w-full">
                  <div className="relative h-48 bg-dark-900/50 rounded-xl border border-cyan-500/10 overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        <div className="w-24 h-24 rounded-full bg-cyan-500/20 flex items-center justify-center animate-pulse-slow">
                          <div className="w-16 h-16 rounded-full bg-cyan-500/30 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-cyan-400 flex items-center justify-center">
                              <Database className="w-4 h-4 text-dark-950" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
